import { lazy, Component, type ReactNode } from 'react'

/**
 * Regex matching common chunk/module-loading errors that occur when
 * a user has a stale JS bundle cached and the server has deployed
 * a newer build (so old chunk filenames no longer exist). Covers the
 * explicit loader failures and React.lazy's internal `_result.default`
 * access on a broken module.
 *
 * Factory-level access on an undefined module (e.g. the old pattern
 * `.then(m => ({ default: m.SomeExport }))` where `m` was undefined)
 * used to surface with browser-specific phrasings that were impossible
 * to match reliably. We now prevent those by having `lazyWithRetry`
 * defensively throw a typed `ChunkLoadError` when the export is
 * missing; the retry path keys on `err.name` in addition to this regex.
 */
export const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Loading chunk .* failed|Importing a module script failed|Unexpected token '<'|expected expression, got '<'|can't access property "default", e\._result is undefined|undefined is not an object \(evaluating 'e\._result\.default'\)|Cannot read properties of undefined \(reading 'default'\)/

/** Typed error thrown by lazyWithRetry when an import resolves but is
 *  missing the expected export — caught by the same retry path as
 *  CHUNK_ERROR_RE without depending on stringly message matching. */
export class ChunkLoadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ChunkLoadError'
  }
}

function isChunkError(err: unknown): boolean {
  if (err instanceof ChunkLoadError) return true
  const name = (err as { name?: string })?.name
  if (name === 'ChunkLoadError') return true
  const msg = String(err && ((err as Error).message || err))
  return CHUNK_ERROR_RE.test(msg)
}

/**
 * Unregister all service workers. Used during chunk-error recovery to
 * break the SW cache cycle that causes white-screen loops.
 */
async function unregisterServiceWorkers(): Promise<void> {
  if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
    const regs = await navigator.serviceWorker.getRegistrations()
    for (const r of regs || []) await r.unregister()
  }
}

/**
 * Wrapper around React.lazy that auto-retries on chunk-load failures
 * by unregistering stale service workers and forcing a page reload
 * (once per session per key).
 *
 * Overloads support two factory shapes:
 *   1. `() => import('./X')` + `exportName` — preferred, picks the
 *      named export defensively and throws `ChunkLoadError` if the
 *      import resolves without the expected member.
 *   2. `() => Promise<{ default: T }>` — legacy default-export shape.
 *
 * The defensive check eliminates the browser-specific "evaluating
 * 'e.Export'" / "reading 'Export'" crashes that used to slip past the
 * retry logic, because we throw a deterministic error *before* React's
 * lazy initializer ever sees an undefined default.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  key?: string,
): React.LazyExoticComponent<T>
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<Record<string, unknown>>,
  key: string,
  exportName: string,
): React.LazyExoticComponent<T>
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<Record<string, unknown>>,
  key = 'default',
  exportName?: string,
) {
  const resolve = () =>
    factory().then((mod) => {
      if (!mod) {
        throw new ChunkLoadError(
          `Dynamic import for "${key}" resolved to an empty module`,
        )
      }
      if (exportName) {
        const picked = (mod as Record<string, unknown>)[exportName]
        if (picked == null) {
          throw new ChunkLoadError(
            `Dynamic import for "${key}" is missing export "${exportName}"`,
          )
        }
        return { default: picked as T }
      }
      return mod as { default: T }
    })

  return lazy(() =>
    resolve().catch(async (err) => {
      if (isChunkError(err)) {
        const k = `__chunk_retry_${key}`
        const tried =
          typeof sessionStorage !== 'undefined'
            ? sessionStorage.getItem(k)
            : '0'
        if (!tried) {
          try {
            sessionStorage.setItem(k, '1')
          } catch {
            /* noop */
          }
          // Unregister stale SW to break the cache cycle
          await unregisterServiceWorkers()
          const url = new URL(window.location.href)
          url.searchParams.set('v', Date.now().toString())
          window.location.replace(url.toString())
        }
      }
      throw err
    }),
  )
}

interface ChunkGuardBoundaryProps {
  children: ReactNode
  storageKey?: string
  fallback?: ReactNode
}

interface ChunkGuardBoundaryState {
  hasError: boolean
  error: Error | null
  isChunkError: boolean
}

/**
 * Error boundary that catches chunk-load errors from React.lazy components
 * and either auto-reloads (once, after unregistering stale SWs) or renders
 * a user-facing error screen.
 *
 * Chunk errors show "Update verfügbar" (blue); other runtime errors show
 * "Fehler aufgetreten" (red).
 */
export class ChunkGuardBoundary extends Component<
  ChunkGuardBoundaryProps,
  ChunkGuardBoundaryState
> {
  constructor(props: ChunkGuardBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, isChunkError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, isChunkError: isChunkError(error) }
  }

  componentDidCatch(error: Error) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const S = require('@sentry/react')
      S.captureException(error)
    } catch {
      /* noop */
    }
    if (isChunkError(error)) {
      const k = this.props.storageKey || '__chunk_guard_reload'
      const tried =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem(k)
          : '0'
      if (!tried) {
        try {
          sessionStorage.setItem(k, '1')
        } catch {
          /* noop */
        }
        // Unregister stale SW before reloading
        if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
          navigator.serviceWorker.getRegistrations().then(regs => {
            for (const r of (regs || [])) r.unregister()
          })
        }
        const url = new URL(window.location.href)
        url.searchParams.set('v', Date.now().toString())
        window.location.replace(url.toString())
      }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const isChunk = this.state.isChunkError
    // Clear retry flag for chunk errors so user can re-trigger recovery
    if (isChunk) {
      const k = this.props.storageKey || '__chunk_guard_reload'
      try { sessionStorage.removeItem(k) } catch { /* noop */ }
    }

    // For chunk errors caught by a parent boundary with a custom fallback,
    // defer to the parent's fallback prop
    if (isChunk && this.props.fallback) return this.props.fallback

    // Full-screen error UI (German)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className={`mx-auto mb-3 h-12 w-12 rounded-full flex items-center justify-center text-xl ${isChunk ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>!</div>
            <div className="text-gray-900 text-lg font-semibold mb-1">{isChunk ? 'Update verfügbar' : 'Fehler aufgetreten'}</div>
            <div className="text-gray-500 mb-4 text-sm">{isChunk ? 'Eine neue Version von FlexWise ist verfügbar. Bitte laden Sie die Seite neu.' : 'Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu.'}</div>
            <button
              onClick={() => { try { sessionStorage.clear() } catch {} window.location.reload() }}
              className={`${isChunk ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'} text-white px-5 py-2 rounded-lg text-sm font-medium`}
            >
              Neu laden
            </button>
          </div>
        </div>
      </div>
    )
  }
}
